import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { Link as LinkDeRota, useNavigate, useSearchParams } from 'react-router-dom';

import { caminhoDaTriagem } from '../../shared/navegacao/mapaDeNavegacao';
import { formatarDataHora } from '../../shared/dominio/datas';
import { EstadoDeCarregamento } from '../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../shared/ui/EstadoDeErro';
import { EstadoVazio } from '../../shared/ui/EstadoVazio';
import { Identificador } from '../../shared/ui/Identificador';
import { opcoesDasOperadoras, type Operadora } from '../../shared/operadoras/operadoras';
import type { MessageKindFilter } from '@anery/api-contracts';

import { FaixaDaDemo } from './demo/FaixaDaDemo';
import { FaixaDeFalhasDeEmail } from './FaixaDeFalhasDeEmail';
import { IdentificacaoDaSolicitacao } from './IdentificacaoDaSolicitacao';
import {
  FILTROS_DE_MODALIDADE,
  TAMANHO_DE_PAGINA,
  opcoesDaFilaDeTriagem,
  type FiltroDeModalidade,
  type FiltrosDaFila,
} from './api/filaDeTriagem';
import {
  ROTULOS_DE_CLASSE_DO_TIPO,
  ROTULOS_DE_ORIGEM_DO_PRAZO,
  ROTULOS_DE_SITUACAO_DO_PRAZO,
  ROTULOS_DE_TIPO_DE_MENSAGEM,
  type CampoDaTriagem,
  type ClassificacaoDaTriagem,
  type TicketDaTriagem,
  type TipoDeMensagem,
} from '../../shared/triagem/tipos';

const ROTULOS_DO_FILTRO_DE_TIPO: Record<MessageKindFilter, string> = {
  ...ROTULOS_DE_TIPO_DE_MENSAGEM,
  Unclassified: 'Não classificado',
};

function ehFiltroDeTipo(valor: string | null): valor is MessageKindFilter {
  return valor !== null && Object.hasOwn(ROTULOS_DO_FILTRO_DE_TIPO, valor);
}

/** Valor de `?operadora=` que desliga o filtro de operadora (inclusive o padrão). */
const TODAS_AS_OPERADORAS = 'todas';

/**
 * O piloto é do Bradesco: sem escolha na URL, a fila abre filtrada nele. O cadastro (`GET /operadoras`) só
 * traz id e nome, e o id muda por ambiente, então o padrão é achado pelo nome. Cadastro sem Bradesco, com mais de um
 * (a escolha dependeria da ordem da API) ou que não carregou abre com todas as operadoras.
 */
function operadoraPadrao(operadoras: readonly Operadora[]): Operadora | null {
  const candidatas = operadoras.filter((operadora) => /bradesco/i.test(operadora.nome));
  return candidatas.length === 1 ? (candidatas[0] ?? null) : null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * `?operadora=` como a tela entende: `null` = sem escolha (vale o padrão), `todas`, ou um id. Valor que não é
 * id nem `todas` conta como sem escolha: mandá-lo à API daria 400 na fila inteira.
 */
function escolhaDaOperadora(valor: string | null): string | null {
  if (valor === TODAS_AS_OPERADORAS) return valor;
  return valor !== null && UUID.test(valor) ? valor : null;
}

const ROTULOS_DO_FILTRO_DE_MODALIDADE: Record<FiltroDeModalidade, string> = {
  AD: 'AD · atendimento domiciliar',
  ID: 'ID · internação domiciliar',
  VM: 'VM · ventilação mecânica',
  sem: 'Sem modalidade',
};

function ehFiltroDeModalidade(valor: string | null): valor is FiltroDeModalidade {
  return valor !== null && (FILTROS_DE_MODALIDADE as readonly string[]).includes(valor);
}

type ChaveDoFiltro =
  | 'somenteAConfirmar'
  | 'somenteAConferir'
  | 'tipoMensagem'
  | 'ordem'
  | 'operadora'
  | 'modalidade';

/** Filtro e página vivem na URL — mesmo motivo de `useFiltrosNaUrl` em `casos`. */
function useFiltrosDaFilaNaUrl(): {
  filtros: Omit<FiltrosDaFila, 'operadoraId'>;
  /** O que a URL escolheu de operadora: `null` (vale o padrão), `todas` ou um id. */
  operadoraEscolhida: string | null;
  definirPagina: (valor: number) => void;
  definirFiltro: (chave: ChaveDoFiltro, valor: string | null) => void;
  limparFiltros: () => void;
} {
  const [parametros, definirParametros] = useSearchParams();

  const filtros = useMemo<Omit<FiltrosDaFila, 'operadoraId'>>(() => {
    const numero = Number(parametros.get('pagina'));
    const tipo = parametros.get('tipoMensagem');
    const modalidade = parametros.get('modalidade');
    return {
      pagina: Number.isInteger(numero) && numero >= 1 ? numero : 1,
      somenteAConfirmar: parametros.get('somenteAConfirmar') === '1',
      tipoMensagem: ehFiltroDeTipo(tipo) ? tipo : null,
      maisRecentesPrimeiro: parametros.get('ordem') === 'recentes',
      somenteAConferirTipoEOperadora: parametros.get('somenteAConferir') === '1',
      modalidade: ehFiltroDeModalidade(modalidade) ? modalidade : null,
    };
  }, [parametros]);
  const operadoraEscolhida = escolhaDaOperadora(parametros.get('operadora'));

  const definirPagina = useCallback(
    (valor: number) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (valor <= 1) proximos.delete('pagina');
          else proximos.set('pagina', String(valor));
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  const definirFiltro = useCallback(
    (chave: ChaveDoFiltro, valor: string | null) => {
      definirParametros(
        (atual) => {
          const proximos = new URLSearchParams(atual);
          if (valor) proximos.set(chave, valor);
          else proximos.delete(chave);
          // Trocar o filtro volta para a primeira página — a mesma razão
          // de `useFiltrosNaUrl`: manter a página 3 mostraria vazio.
          proximos.delete('pagina');
          return proximos;
        },
        { replace: true },
      );
    },
    [definirParametros],
  );

  // Uma escrita só: dois `definirParametros` no mesmo clique perdem o
  // primeiro (os dois partem da mesma URL). A operadora vira `todas`, e não
  // some da URL: sem ela, voltaria o padrão (Bradesco), e "ver todos" não
  // mostraria todos.
  const limparFiltros = useCallback(() => {
    definirParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        proximos.delete('somenteAConfirmar');
        proximos.delete('somenteAConferir');
        proximos.delete('tipoMensagem');
        proximos.delete('modalidade');
        proximos.set('operadora', TODAS_AS_OPERADORAS);
        proximos.delete('pagina');
        return proximos;
      },
      { replace: true },
    );
  }, [definirParametros]);

  return { filtros, operadoraEscolhida, definirPagina, definirFiltro, limparFiltros };
}

/**
 * `TP-028`: a classe do e-mail que abriu a thread, em RÓTULO ESCRITO (mesma
 * regra do `AC-14` — nunca só cor). `null` é o e-mail ainda não interpretado.
 */
function CelulaDeTipoDeMensagem({ tipo }: { tipo: TipoDeMensagem | null }): React.JSX.Element {
  if (tipo === null) return <Chip label="Não classificado" size="small" variant="outlined" />;
  const cor = tipo === 'Receipt' ? 'success' : tipo === 'Undetermined' ? 'warning' : 'default';
  return <Chip label={ROTULOS_DE_TIPO_DE_MENSAGEM[tipo]} size="small" color={cor} />;
}

/**
 * `AC-7`: campo abaixo do limiar configurado no servidor precisa de rótulo
 * VISÍVEL, não só destaque de cor. Não é `ConfirmacaoHumana` — aquele é
 * diálogo de confirmar AÇÃO; isto é rótulo de campo, categoria diferente.
 */
function RotuloAConfirmar(): React.JSX.Element {
  return <Chip label="A confirmar" size="small" color="warning" variant="outlined" />;
}

function campoAbaixoDoLimiar(campo: CampoDaTriagem): boolean {
  return campo.confianca !== null && campo.limiar !== null && campo.confianca < campo.limiar;
}

/** Texto da célula de um campo lido — nunca célula vazia sem explicação. */
function rotuloDoCampo(campo: CampoDaTriagem, temEmailDeOrigem: boolean): string {
  if (!temEmailDeOrigem) return 'Aguardando e-mail';
  if (!campo.tentado) return 'Não tentado';
  if (!campo.encontrado) return 'Não encontrado';
  return campo.valorHumano ?? campo.valor ?? 'Encontrado, sem valor';
}

function CelulaDeCampo({ campo, temEmailDeOrigem }: { campo: CampoDaTriagem; temEmailDeOrigem: boolean }): React.JSX.Element {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" color={temEmailDeOrigem && !campo.tentado ? 'text.disabled' : undefined}>
        {temEmailDeOrigem && !campo.tentado ? '—' : rotuloDoCampo(campo, temEmailDeOrigem)}
      </Typography>
      {campoAbaixoDoLimiar(campo) ? <RotuloAConfirmar /> : null}
    </Stack>
  );
}

/** O texto que o e-mail trazia, entre aspas. `null` se nada foi lido. */
/**
 * `TP-038`: o que a plataforma entende do tipo e da operadora lidos do e-mail de origem, em palavras da operação.
 * Em RÓTULO ESCRITO (mesma regra do `AC-14`: nunca só cor). `classificacao: null` é "ainda não lido" e nunca um tipo
 * vazio; "Conferir" é o que o parser não resolveu sozinho.
 *
 * É também a coluna do que o interpretador leu de tipo e operadora (`AC-2`): o texto do e-mail aparece sempre, ao
 * lado do que a plataforma entendeu, e a marca "A confirmar" de cada um dos dois campos (`AC-7`) mora aqui. Por isso
 * a fila não tem colunas cruas dos dois campos.
 */
function CelulaDeClassificacao({
  classificacao,
  temEmailDeOrigem,
  tipoLido,
  operadoraLida,
}: {
  classificacao: ClassificacaoDaTriagem | null;
  temEmailDeOrigem: boolean;
  tipoLido: CampoDaTriagem;
  operadoraLida: CampoDaTriagem;
}): React.JSX.Element {
  if (!temEmailDeOrigem) return <Typography variant="body2">Aguardando e-mail</Typography>;
  const aConfirmar = campoAbaixoDoLimiar(tipoLido) || campoAbaixoDoLimiar(operadoraLida);
  if (classificacao === null) {
    // Lido mas ainda não classificado (o passo de classificação não rodou) é diferente de nada lido.
    const lido = tipoLido.tentado || operadoraLida.tentado;
    // Nada tentado: duas linhas "Não tentado" repetiriam o que a etiqueta já diz.
    if (!lido) return <Chip label="Ainda não lido" size="small" variant="outlined" />;
    return (
      <Stack spacing={0.25} alignItems="flex-start">
        <Typography variant="body2">{rotuloDoCampo(tipoLido, temEmailDeOrigem)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {rotuloDoCampo(operadoraLida, temEmailDeOrigem)}
        </Typography>
        {aConfirmar ? <RotuloAConfirmar /> : null}
        <Chip label="Ainda não classificado" size="small" variant="outlined" />
      </Stack>
    );
  }

  return <LeituraDaClassificacao classificacao={classificacao} aConfirmar={aConfirmar} />;
}

/**
 * O que a plataforma entendeu, em três linhas: tipo (com a modalidade ao lado), operadora e a marca de conferir. A
 * barra lateral na cor de alerta só acompanha o rótulo escrito "Conferir" (`AC-14`: nunca só cor); o texto inteiro ("tipo e operadora") está no title e no detalhe.
 * O que o e-mail dizia, quem leu e o motivo da dúvida estão no detalhe do ticket: a fila é para varrer, não para ler.
 */
function LeituraDaClassificacao({
  classificacao,
  aConfirmar,
}: {
  classificacao: ClassificacaoDaTriagem;
  aConfirmar: boolean;
}): React.JSX.Element {
  const nomeDaOperadora = classificacao.operadora?.nome ?? 'Operadora não identificada';
  const rotuloDoTipo =
    classificacao.classeDoTipo === null ? 'Tipo não informado' : ROTULOS_DE_CLASSE_DO_TIPO[classificacao.classeDoTipo];
  return (
    <Stack
      spacing={0.25}
      alignItems="flex-start"
      sx={{
        pl: 0.75,
        borderLeft: 3,
        borderColor: classificacao.aConferir ? 'warning.main' : 'transparent',
      }}
    >
      {/* A modalidade (AD ou ID) já vem no próprio rótulo do tipo, e a dúvida na marca abaixo: sem chip repetido. */}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {rotuloDoTipo}
      </Typography>
      <Typography variant="body2" color="text.secondary" title={nomeDaOperadora} sx={DUAS_LINHAS}>
        {nomeDaOperadora}
      </Typography>
      {classificacao.aConferir || aConfirmar ? (
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          {classificacao.aConferir ? (
            <Chip
              label="Conferir"
              title="Conferir tipo e operadora"
              size="small"
              color="warning"
              variant="outlined"
            />
          ) : null}
          {aConfirmar ? <RotuloAConfirmar /> : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

// A linha inteira abre o ticket; o link do código continua para teclado e para abrir em outra aba. Não abre quando o
// clique foi num link ou botão (que têm a própria ação) nem quando a pessoa estava selecionando texto para copiar.
function cliqueEmControleOuSelecao(alvo: EventTarget): boolean {
  if (alvo instanceof Element && alvo.closest('a, button, input, [role="button"]') !== null) return true;
  return (window.getSelection()?.toString() ?? '') !== '';
}

// A tabela tem dez colunas e várias células empilham texto, chip e legenda: no tamanho padrão do MUI ela passa de
// 1700 px e a janela não mostra as colunas da direita. Um degrau a menos de letra e de espaço, sem trocar cor nem fonte.
const TABELA_COMPACTA = {
  '& .MuiTableCell-root': { px: 1, py: 0.75, fontSize: '0.8125rem' },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiTypography-caption': { fontSize: '0.7rem' },
  // Só a etiqueta da coluna Ticket ("Atualizada · data · N mensagens") é longa o bastante para quebrar em vez de alargar
  // a coluna; as demais ("A confirmar", "Vencido") ficam numa linha só.
  '& td:first-of-type .MuiChip-root': { height: 'auto', maxWidth: '100%' },
  '& td:first-of-type .MuiChip-label': { whiteSpace: 'normal', py: 0.25 },
  // O ticket fica à vista enquanto as colunas da direita rolam.
  '& th:first-of-type, & td:first-of-type': { position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' },
} as const;

const DUAS_LINHAS = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
} as const;

// O assunto é o campo que mais cresce (nome, CPF e número no próprio e-mail): duas linhas, e o texto inteiro ao pousar o mouse.
function AssuntoDoTicket({ ticket }: { ticket: TicketDaTriagem }): React.JSX.Element {
  const texto = ticket.assunto ?? (ticket.temEmailDeOrigem ? 'Sem assunto' : 'Aguardando e-mail');
  return (
    <Typography variant="body2" title={texto} sx={DUAS_LINHAS}>
      {texto}
    </Typography>
  );
}

function CelulaDePrazo({ ticket }: { ticket: TicketDaTriagem }): React.JSX.Element {
  const { prazo } = ticket;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">{prazo.valor === null ? 'Sem prazo' : formatarDataHora(prazo.valor)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {ROTULOS_DE_ORIGEM_DO_PRAZO[prazo.origem]}
      </Typography>
      {/* AC-14: situação do prazo em ROTULO ESCRITO, nunca só cor/ícone. */}
      <Chip label={ROTULOS_DE_SITUACAO_DO_PRAZO[prazo.situacao]} size="small" variant="outlined" />
    </Stack>
  );
}

/**
 * A fila de triagem (`AC-1`-`AC-6`, `AC-10`).
 *
 * Quatro estados, todos testados: carregando, erro, vazio e dados. Erro
 * SEMPRE antes da checagem de vazio — API fora do ar nunca vira "nenhuma
 * triagem aberta".
 *
 * A ordenação é a que a API devolve (prioridade comercial, `AC-3`): esta
 * tela não reordena no client, e não existe coluna clicável para ordenar
 * por prazo — a story proíbe isso explicitamente.
 */
export function FilaDeTriagem(): React.JSX.Element {
  const navegar = useNavigate();
  const { filtros: filtrosDaUrl, operadoraEscolhida, definirPagina, definirFiltro, limparFiltros } =
    useFiltrosDaFilaNaUrl();
  const operadoras = useQuery(opcoesDasOperadoras());
  const opcoesDeOperadora = operadoras.data ?? [];
  const padrao = operadoraPadrao(opcoesDeOperadora);
  // Sem escolha na URL, o filtro só se decide quando o cadastro responde: buscar antes mostraria todas as
  // operadoras e, um instante depois, só o Bradesco.
  const esperandoOPadrao = operadoraEscolhida === null && operadoras.isPending;
  const operadoraId =
    operadoraEscolhida === TODAS_AS_OPERADORAS ? null : (operadoraEscolhida ?? padrao?.id ?? null);
  const filtros: FiltrosDaFila = { ...filtrosDaUrl, operadoraId };
  const { pagina, somenteAConfirmar } = filtros;
  const consulta = useQuery({ ...opcoesDaFilaDeTriagem(filtros), enabled: !esperandoOPadrao });
  const algumFiltro =
    somenteAConfirmar ||
    filtros.somenteAConferirTipoEOperadora ||
    filtros.tipoMensagem !== null ||
    filtros.modalidade !== null ||
    operadoraId !== null;
  // Id na URL que o cadastro não traz (operadora desativada, link antigo): continua escolhido e visível.
  const escolhidaForaDoCadastro =
    operadoraId !== null && operadoras.isSuccess && !opcoesDeOperadora.some((o) => o.id === operadoraId);
  const definirSomenteAConfirmar = (valor: boolean): void => {
    definirFiltro('somenteAConfirmar', valor ? '1' : null);
  };

  const fila = consulta.data;
  const itens = fila?.itens ?? [];
  const temProximaPagina = fila !== undefined && pagina * TAMANHO_DE_PAGINA < fila.total;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h1">Fila de triagem</Typography>
        <Typography variant="body2" color="text.secondary">
          O que chegou por e-mail e precisa de conferência. Quando o pedido está completo, ele vira Caso.
        </Typography>
        {/* Só aparece quando a API liga a leitura da caixa real. */}
        <FaixaDaDemo />
        {/* Falha de triagem não tem ticket e some desta fila: o aviso diz que há e-mail parado. */}
        <FaixaDeFalhasDeEmail />
      </Box>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
          <TextField
            select
            label="Operadora"
            value={operadoraId ?? TODAS_AS_OPERADORAS}
            onChange={(evento) => {
              definirFiltro('operadora', evento.target.value);
            }}
            disabled={esperandoOPadrao}
            helperText={operadoraEscolhida === null && padrao !== null ? 'Padrão do piloto' : undefined}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value={TODAS_AS_OPERADORAS}>Todas as operadoras</MenuItem>
            {opcoesDeOperadora.map((operadora) => (
              <MenuItem key={operadora.id} value={operadora.id}>
                {operadora.nome}
              </MenuItem>
            ))}
            {escolhidaForaDoCadastro ? (
              <MenuItem value={operadoraId}>Operadora fora do cadastro ativo</MenuItem>
            ) : null}
          </TextField>
          <TextField
            select
            label="Modalidade"
            value={filtros.modalidade ?? ''}
            onChange={(evento) => {
              definirFiltro('modalidade', evento.target.value || null);
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Todas as modalidades</MenuItem>
            {FILTROS_DE_MODALIDADE.map((modalidade) => (
              <MenuItem key={modalidade} value={modalidade}>
                {ROTULOS_DO_FILTRO_DE_MODALIDADE[modalidade]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Campo a confirmar"
            value={somenteAConfirmar ? '1' : ''}
            onChange={(evento) => {
              definirSomenteAConfirmar(evento.target.value === '1');
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Todos os tickets</MenuItem>
            <MenuItem value="1">Só com campo a confirmar</MenuItem>
          </TextField>
          <TextField
            select
            label="Classificação"
            value={filtros.somenteAConferirTipoEOperadora ? '1' : ''}
            onChange={(evento) => {
              definirFiltro('somenteAConferir', evento.target.value || null);
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Todos os tickets</MenuItem>
            <MenuItem value="1">Só com tipo ou operadora a conferir</MenuItem>
          </TextField>
          <TextField
            select
            label="Tipo de mensagem"
            value={filtros.tipoMensagem ?? ''}
            onChange={(evento) => {
              definirFiltro('tipoMensagem', evento.target.value || null);
            }}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Todos os tipos</MenuItem>
            {(Object.keys(ROTULOS_DO_FILTRO_DE_TIPO) as MessageKindFilter[]).map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {ROTULOS_DO_FILTRO_DE_TIPO[tipo]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Ordem"
            value={filtros.maisRecentesPrimeiro ? 'recentes' : ''}
            onChange={(evento) => {
              definirFiltro('ordem', evento.target.value || null);
            }}
            helperText={filtros.maisRecentesPrimeiro ? undefined : 'Padrão da operação (BR-004)'}
            sx={{ minWidth: 240 }}
          >
            <MenuItem value="">Prioridade comercial</MenuItem>
            <MenuItem value="recentes">Mais recentes primeiro</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <Paper variant="outlined">
        {consulta.isPending ? <EstadoDeCarregamento /> : null}

        {consulta.isError ? (
          <EstadoDeErro
            erro={consulta.error}
            contexto="Não foi possível carregar a fila de triagem"
            aoReprocessar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}

        {consulta.isSuccess && itens.length === 0 ? (
          <EstadoVazio
            titulo={algumFiltro ? 'Nenhuma triagem com esses filtros' : 'Nenhuma triagem aberta'}
            descricao={
              algumFiltro
                ? 'A API respondeu normalmente; nenhum ticket desta página passa nos filtros escolhidos.'
                : 'A API respondeu normalmente e não há ticket de triagem aberto nesta página.'
            }
            {...(algumFiltro
              ? {
                  acao: {
                    rotulo: 'Ver todos os tickets',
                    aoClicar: limparFiltros,
                  },
                }
              : {})}
          />
        ) : null}

        {consulta.isSuccess && itens.length > 0 ? (
          <TableContainer>
            <Table aria-label="Fila de triagem" size="small" sx={TABELA_COMPACTA}>
              <TableHead>
                <TableRow>
                  <TableCell>Ticket</TableCell>
                  <TableCell>Núcleo</TableCell>
                  <TableCell>Chegada</TableCell>
                  <TableCell>Assunto</TableCell>
                  <TableCell>Tipo de mensagem</TableCell>
                  <TableCell>Tipo e operadora</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Hospital</TableCell>
                  <TableCell>Mensagens</TableCell>
                  <TableCell>Prazo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    onClick={(evento) => {
                      if (cliqueEmControleOuSelecao(evento.target)) return;
                      void navegar(caminhoDaTriagem(ticket.id));
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ maxWidth: 190 }}>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <LinkDeRota to={caminhoDaTriagem(ticket.id)} style={{ textDecoration: 'none' }}>
                          <Identificador valor={ticket.codigoCurto ?? ticket.id} />
                        </LinkDeRota>
                        <IdentificacaoDaSolicitacao
                          numeroSiai={ticket.numeroSiai}
                          contagemDeMensagens={ticket.contagemDeMensagens}
                          ultimaMensagemEm={ticket.ultimaMensagemEm}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {ticket.nomeDoNucleo ?? (ticket.nucleo === null ? '—' : <Identificador valor={ticket.nucleo} />)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 80 }}>
                      {ticket.chegadaEm === null ? '—' : formatarDataHora(ticket.chegadaEm)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <AssuntoDoTicket ticket={ticket} />
                    </TableCell>
                    <TableCell>
                      <CelulaDeTipoDeMensagem tipo={ticket.tipoDeMensagem} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <CelulaDeClassificacao
                        classificacao={ticket.classificacao}
                        temEmailDeOrigem={ticket.temEmailDeOrigem}
                        tipoLido={ticket.tipoDeSolicitacao}
                        operadoraLida={ticket.operadora}
                      />
                    </TableCell>
                    <TableCell>
                      <CelulaDeCampo campo={ticket.paciente} temEmailDeOrigem={ticket.temEmailDeOrigem} />
                    </TableCell>
                    <TableCell>
                      <CelulaDeCampo campo={ticket.hospital} temEmailDeOrigem={ticket.temEmailDeOrigem} />
                    </TableCell>
                    <TableCell>{ticket.contagemDeMensagens}</TableCell>
                    <TableCell>
                      <CelulaDePrazo ticket={ticket} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Paper>

      {/* Paginação REAL — `total` existe no envelope, ao contrário de `/casos`. */}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          disabled={pagina <= 1}
          onClick={() => {
            definirPagina(pagina - 1);
          }}
        >
          Página anterior
        </Button>
        <Typography variant="body2" color="text.secondary">
          Página {pagina}
          {fila !== undefined ? ` de ${String(Math.max(1, Math.ceil(fila.total / TAMANHO_DE_PAGINA)))} — ${String(fila.total)} no total` : ''}
        </Typography>
        <Button
          variant="outlined"
          disabled={!temProximaPagina}
          onClick={() => {
            definirPagina(pagina + 1);
          }}
        >
          Próxima página
        </Button>
      </Stack>
    </Stack>
  );
}
